import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import Guacamole from 'guacamole-common-js';
import { getServerInfo } from '../../../utils/helpers/networkInfo.helper';  // Importamos la función para obtener la URL
import { useLocation } from 'react-router-dom';
import { clearLinePDU } from '../../../services/PDU';

const SessionContext = createContext();

// Hook para acceder a las funciones del SessionManager
export const useSessionManager = () => useContext(SessionContext);

// IMPORTANTE: Guacamole.Client y Guacamole.WebSocketTunnel son de un solo uso.
// Llamar connect() de nuevo sobre un cliente/túnel ya usado abre un segundo WebSocket
// que comparte estado con el anterior (cuando el viejo se cierra, cierra el nuevo).
// Por eso cada conexión SIEMPRE crea un cliente y un túnel nuevos y destruye el anterior.
export const SessionManager = ({ children }) => {
  const [sessions, setSessions] = useState({});  // Estado para la UI (se deriva de sessionsRef)
  const [errorMessage, setErrorMessage] = useState(null);
  const [remoteClipboards, setRemoteClipboards] = useState({});  // Último texto copiado en cada máquina

  const MAX_RETRIES = 2;  // Máximo número de reintentos
  const RETRY_DELAY = 1000;  // Delay inicial en milisegundos
  const retryCountRef = useRef({});  // Contador de reintentos por cada vmId
  const isClientConnectedRef = useRef({}); // Guardar el estado de conexión por vmId
  const sessionsRef = useRef({});  // Fuente de verdad: vmId -> { client, tunnel }
  const attemptRef = useRef({});  // Id del intento vigente por vmId; invalida intentos viejos
  const retryTimerRef = useRef({});  // Timers de reintento pendientes por vmId

  const location = useLocation();

  const nextAttempt = (vmId) => {
    attemptRef.current[vmId] = (attemptRef.current[vmId] || 0) + 1;
    return attemptRef.current[vmId];
  };

  // Cierra y descarta el cliente/túnel de una VM sin importar en qué estado esté
  const destroySession = useCallback((vmId) => {
    clearTimeout(retryTimerRef.current[vmId]);
    delete retryTimerRef.current[vmId];

    const session = sessionsRef.current[vmId];
    delete sessionsRef.current[vmId];
    isClientConnectedRef.current[vmId] = false;

    if (session) {
      const { client, tunnel } = session;
      // Quitar handlers para que el cierre no dispare reintentos ni cambios de estado
      client.onstatechange = null;
      client.onerror = null;
      client.onclipboard = null;
      tunnel.onstatechange = null;
      tunnel.onerror = null;
      try {
        client.disconnect();
      } catch (e) { /* el cliente puede no haber llegado a conectar */ }
      try {
        // client.disconnect() no hace nada si el cliente ya estaba DISCONNECTED; asegurar el cierre del túnel
        tunnel.disconnect();
      } catch (e) { /* el túnel puede no haber abierto socket */ }
    }

    setSessions((prev) => {
      if (!(vmId in prev)) return prev;
      const newSessions = { ...prev };
      delete newSessions[vmId];
      return newSessions;
    });
  }, []);

  const disconnectClient = useCallback((vmId) => {
    nextAttempt(vmId);  // Cancela cualquier startSession o reintento en curso
    destroySession(vmId);
  }, [destroySession]);

  const disconnectAllClients = useCallback(() => {
    Object.keys(sessionsRef.current).forEach((vmId) => {
      disconnectClient(vmId);  // Desconecta todos los clientes
    });
  }, [disconnectClient]);

  const connectClient = useCallback((vmId, encryptedToken, attemptId, retryDelay = RETRY_DELAY) => {
    // Nunca reutilizar un cliente anterior
    destroySession(vmId);

    const isCurrent = () => attemptRef.current[vmId] === attemptId;
    let hasConnected = false;

    const updateSession = (patch) => {
      if (!isCurrent()) return;
      setSessions((prev) => (prev[vmId] ? { ...prev, [vmId]: { ...prev[vmId], ...patch } } : prev));
    };

    const scheduleRetry = () => {
      // Solo reintentar si este intento nunca llegó a conectar
      if (!isCurrent() || hasConnected) return;
      if (retryCountRef.current[vmId] < MAX_RETRIES) {
        retryCountRef.current[vmId] += 1;
        clearTimeout(retryTimerRef.current[vmId]);
        retryTimerRef.current[vmId] = setTimeout(() => {
          if (isCurrent()) {
            connectClient(vmId, encryptedToken, attemptId, retryDelay * 2);  // Retardo exponencial
          }
        }, retryDelay);
      } else {
        setErrorMessage(`Se alcanzó el número máximo de reintentos para ${vmId}.`);
      }
    };

    try {
      const serverUrl = getServerInfo('ws');
      const url = `${serverUrl.replace('http', 'ws')}?token=${encryptedToken}`;

      const tunnel = new Guacamole.WebSocketTunnel(url);
      const client = new Guacamole.Client(tunnel);

      sessionsRef.current[vmId] = { client, tunnel };

      setSessions((prev) => ({
        ...prev,
        [vmId]: {
          vmId,
          encryptedToken,
          client,
          tunnel,
          connected: false,
          statusClient: '-',
        },
      }));

      // Texto que se copia dentro de la máquina remota
      client.onclipboard = (stream, mimetype) => {
        if (!isCurrent() || !/^text\//.test(mimetype)) return;
        const reader = new Guacamole.StringReader(stream);
        let data = '';
        reader.ontext = (text) => {
          data += text;
        };
        reader.onend = () => {
          if (!isCurrent()) return;
          setRemoteClipboards((prev) => ({ ...prev, [vmId]: data }));
        };
      };

      client.onerror = (error) => {
        if (!isCurrent()) return;
        setErrorMessage(`Error en conexión: ${error.message}`);
        scheduleRetry();
      };

      // Guacamole.Client no escucha el cierre del túnel: detectarlo aquí
      tunnel.onerror = (status) => {
        if (!isCurrent()) return;
        setErrorMessage(`Error en conexión: ${status.message}`);
        scheduleRetry();
      };

      tunnel.onstatechange = (state) => {
        if (!isCurrent() || state !== Guacamole.Tunnel.State.CLOSED) return;
        isClientConnectedRef.current[vmId] = false;
        setErrorMessage(`desconectado`);
        updateSession({ connected: false, statusClient: 'Desconectado' });
        // Detener keep-alive del cliente; el túnel ya está cerrado
        try {
          client.disconnect();
        } catch (e) { /* ya desconectado */ }
      };

      client.onstatechange = (state) => {
        if (!isCurrent()) return;
        switch (state) {
          case Guacamole.Client.State.IDLE:
            setErrorMessage(`inactivo`);
            updateSession({ connected: false, statusClient: 'Inactivo' });
            break;
          case Guacamole.Client.State.CONNECTING:
            setErrorMessage(`conectando`);
            updateSession({ connected: false, statusClient: 'Conectando' });
            break;
          case Guacamole.Client.State.WAITING:
            setErrorMessage(`esperando`);
            updateSession({ connected: false, statusClient: 'Esperando' });
            break;
          case Guacamole.Client.State.CONNECTED:
            hasConnected = true;
            isClientConnectedRef.current[vmId] = true;
            setErrorMessage(`conectado`);
            updateSession({ connected: true, statusClient: 'Conectado' });
            retryCountRef.current[vmId] = 0;  // Resetear reintentos
            break;
          case Guacamole.Client.State.DISCONNECTING:
            isClientConnectedRef.current[vmId] = false;
            setErrorMessage(`desconectando`);
            updateSession({ connected: false, statusClient: 'Desconectando' });
            break;
          case Guacamole.Client.State.DISCONNECTED:
            isClientConnectedRef.current[vmId] = false;
            setErrorMessage(`desconectado`);
            updateSession({ connected: false, statusClient: 'Desconectado' });
            break;
          default:
            setErrorMessage(`Estado desconocido para ${vmId}: ${state}`);
            break;
        }
      };
      client.connect();
    } catch (error) {
      setErrorMessage(`Error creando el túnel para ${vmId}: ${error.message}`);
      scheduleRetry();
    }
  }, [destroySession]);

  const startSession = async (vmId, encryptedToken, resourceData = null, authToken = null) => {
    // Nuevo intento: invalida intentos anteriores y cierra la sesión previa, si existe
    const attemptId = nextAttempt(vmId);
    destroySession(vmId);

    // Si hay datos del resource, hacer la llamada a clear-line antes de conectar
    if (resourceData) {
      const hostVm = resourceData.host_vm || resourceData.host
      const port = resourceData.port

      if (hostVm && port) {
        try {
          await clearLinePDU(
            {
              host: hostVm,
              username: 'netmask',
              password: 'N3tm45k1!',
              line_number: port, // El port es el line_number
            },
            authToken // token de autenticación
          );
        } catch (error) {
          console.error(`Error al limpiar línea PDU para VM ${vmId}:`, error);
          // Continuar con la conexión aunque falle el clear-line
        }
      }
    }

    // Si mientras se limpiaba la línea hubo otro startSession o un disconnect, abortar
    if (attemptRef.current[vmId] !== attemptId) return;

    retryCountRef.current[vmId] = 0;  // Inicializar el contador de reintentos para la sesión
    connectClient(vmId, encryptedToken, attemptId);  // Siempre un cliente nuevo
  };

  // Función para detener una sesión Guacamole
  const stopSession = (vmId) => {
    disconnectClient(vmId);
  };

  // Envía texto al portapapeles de la máquina remota (luego se pega dentro con Ctrl+V)
  const sendClipboard = useCallback((vmId, text) => {
    const session = sessionsRef.current[vmId];
    if (!session || !isClientConnectedRef.current[vmId] || !text) return false;
    const stream = session.client.createClipboardStream('text/plain');
    const writer = new Guacamole.StringWriter(stream);
    writer.sendText(text);
    writer.sendEnd();
    return true;
  }, []);

  const getRemoteClipboard = (vmId) => remoteClipboards[vmId] || '';

  // Función para obtener una sesión activa
  const getSession = (vmId) => ({
    ...sessions[vmId],
    isConnected: isClientConnectedRef.current[vmId], // Añadir el estado de conexión
  });

  // Efecto para desconectar todas las sesiones cuando cambia la ruta
  useEffect(() => {
    if (location.pathname === "/successviewLab") {
      disconnectAllClients();
    }
  }, [location, disconnectAllClients]);  // Se ejecuta cada vez que cambia la ubicación (ruta)

  // Cerrar todas las conexiones al cerrar/recargar la pestaña o desmontar el provider
  useEffect(() => {
    window.addEventListener('beforeunload', disconnectAllClients);
    return () => {
      window.removeEventListener('beforeunload', disconnectAllClients);
      disconnectAllClients();
    };
  }, [disconnectAllClients]);


  return (
    <SessionContext.Provider value={{ startSession, stopSession, getSession, disconnectAllClients, disconnectClient, errorMessage, sendClipboard, getRemoteClipboard }}>
      {children}
    </SessionContext.Provider>
  );
};
